import { useEffect } from "react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

const Block = ({ title, children }) => (
  <div className="mt-12">
    <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">{title}</h2>
    <div className="space-y-4 text-sm md:text-base text-charcoal-soft leading-relaxed max-w-3xl">{children}</div>
  </div>
);

const PRIVACY = {
  eyebrow: "The Danielle Edit — Legal",
  title: "Privacy Policy",
  updated: "Last updated: October 2026",
  blocks: [
    {
      title: "What this site collects",
      body: [
        "When you send an inquiry through this website, I collect the details you type into the form: your name, your email address, your brand or company name if you choose to share it, the type of project you are considering, your project details, your timeline, and your budget range. The form also records that you acknowledged how project agreements work.",
        "This website does not offer accounts, comments, purchases, or newsletter sign-ups, and it does not collect payment information.",
      ],
    },
    {
      title: "How your information is used",
      body: [
        "Your inquiry details are used for one purpose: to read your note, respond to you, and — if we decide to work together — prepare a proposal and project agreement. Your information is never sold, rented, or shared for advertising.",
      ],
    },
    {
      title: "Storage, email, and service providers",
      body: [
        "This website is hosted on the Emergent platform, which processes the standard technical data required to serve a website (such as server logs). When you submit the inquiry form, your details are stored in the site's database and a notification is sent to the studio through a managed email delivery service.",
        "This site links to external platforms — Instagram and the websites of ASNHVN Group and ANCR Ecosystem. When you follow those links, those platforms' own privacy policies apply.",
      ],
    },
    {
      title: "Cookies and analytics",
      body: [
        "The Danielle Edit does not run advertising cookies or third-party ad trackers. The hosting platform may load its own operational script to keep the site running and measure basic performance.",
      ],
    },
    {
      title: "How long inquiries are kept",
      body: [
        "Inquiry records are kept for as long as needed to manage our conversation and any resulting project, and to maintain a simple history of client relationships. If you would like your inquiry deleted, just ask — email dani@danielleedit.com and it will be removed.",
      ],
    },
    {
      title: "Security",
      body: [
        "This website is served over HTTPS, and access to inquiry records is restricted to the studio.",
      ],
    },
    {
      title: "Your choices",
      body: [
        "You may request a copy of the information you have sent, ask for a correction, or ask for deletion at any time by emailing dani@danielleedit.com or calling 224.663.0427.",
      ],
    },
    {
      title: "Changes to this policy",
      body: [
        "If this policy changes, the updated version will be posted on this page with a new date.",
      ],
    },
  ],
};

const TERMS = {
  eyebrow: "The Danielle Edit — Legal",
  title: "Terms of Use & Client Service Terms",
  updated: "Last updated: October 2026",
  blocks: [
    {
      title: "Using this website",
      body: [
        "The content of this website — including all logos, brand identities, presentation decks, imagery, and text — belongs to The Danielle Edit and its clients, and is displayed here for portfolio purposes. Please don't copy, reuse, or redistribute anything without written permission.",
      ],
    },
    {
      title: "How projects are agreed",
      body: [
        "Sending an inquiry starts a conversation — it does not create a project, a booking, or an obligation on either side. Every engagement begins with a written proposal covering scope, deliverables, timeline, and investment. Work begins only after we both agree to that proposal in writing. Where this page summarizes terms, the written project agreement for your engagement always governs.",
      ],
    },
    {
      title: "Scope and revisions",
      body: [
        "The scope of each project — what is included, how many refinement rounds are part of it, and what counts as additional work — is defined in your written proposal. Work beyond the agreed scope, including extra refinement rounds, is quoted separately before it begins.",
      ],
    },
    {
      title: "Deposits and payment schedules",
      body: [
        "Projects may require a deposit before work begins, with the remaining balance on an agreed schedule. Payment plans are available for select projects; every payment schedule is agreed upon in writing before work begins. Published rates on this website are introductory, and final investment is based on your project's scope.",
      ],
    },
    {
      title: "Cancellations and refunds",
      body: [
        "Either of us may cancel before work begins at no charge. Because brand work is custom creative work, once work has begun, completed work and time already invested are billed, and deposits for work underway are non-refundable, except where your written agreement states otherwise.",
      ],
    },
    {
      title: "Ownership and portfolio display",
      body: [
        "Full ownership of your final approved deliverables transfers to you upon final payment. The Danielle Edit retains the right to display completed work in its portfolio, unless we agree otherwise in writing.",
      ],
    },
    {
      title: "Third-party costs",
      body: [
        "Third-party subscriptions, domains, hosting, phone service, printing, and similar costs are separate from design fees unless your proposal explicitly includes them. Where accounts are set up on your behalf, they are registered in your name wherever possible.",
      ],
    },
    {
      title: "Delivery",
      body: [
        "Final production files are delivered digitally once the project is complete and the final payment has been received.",
      ],
    },
    {
      title: "Contact",
      body: [
        "Questions about these terms: dani@danielleedit.com · 224.663.0427.",
      ],
    },
  ],
};

const LegalPage = ({ doc, testId }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <main data-testid={testId} className="bg-paper text-charcoal min-h-screen">
      <Nav />
      <div className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24 md:pb-32">
        <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute mb-6">{doc.eyebrow}</p>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">{doc.title}</h1>
        <p className="mt-5 text-[11px] uppercase tracking-[0.24em] text-charcoal-mute">{doc.updated}</p>
        <hr className="chrome-rule mt-10 max-w-xs" />
        {doc.blocks.map((b) => (
          <Block key={b.title} title={b.title}>
            {b.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </Block>
        ))}
      </div>
      <Footer />
    </main>
  );
};

export const PrivacyPage = () => <LegalPage doc={PRIVACY} testId="privacy-page" />;
export const TermsPage = () => <LegalPage doc={TERMS} testId="terms-page" />;
